import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Fingerprint, LogOut, Monitor, Plus, Smartphone, Trash2 } from "lucide-react";
import PageHeader, { card, ghostBtn } from "@/app/PageHeader";
import { useSession } from "@/app/session";
import { createPasskey, deviceName, isCancel, passkeyLabel, passkeysSupported } from "@/app/passkey";

const when = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "Never");

// A readable name for a browser's user-agent string ("Safari on iPhone").
const describeAgent = (ua = "") => {
  const device = /iPhone/.test(ua) ? "iPhone" : /iPad/.test(ua) ? "iPad" : /Android/.test(ua) ? "Android"
    : /Macintosh/.test(ua) ? "Mac" : /Windows/.test(ua) ? "Windows" : /Linux/.test(ua) ? "Linux" : "Unknown device";
  const browser = /Edg\//.test(ua) ? "Edge" : /CriOS|Chrome\//.test(ua) ? "Chrome" : /FxiOS|Firefox\//.test(ua) ? "Firefox"
    : /Safari\//.test(ua) ? "Safari" : "Browser";
  return { label: `${browser} on ${device}`, mobile: /iPhone|Android|iPad/.test(ua) };
};

// Face ID / passkeys for one-tap sign-in, and the devices currently signed in.
const Security = () => {
  const { api, onAuthError } = useSession();
  const bio = passkeyLabel();
  const [passkeys, setPasskeys] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/passkeys").then((r) => setPasskeys(r.data.passkeys))
      .catch((err) => onAuthError(err, "Could not load passkeys."));
    api.get("/admin/sessions").then((r) => setSessions(r.data.sessions))
      .catch((err) => onAuthError(err, "Could not load signed-in devices."));
  }, [api, onAuthError]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    setBusy(true);
    try {
      const { data: options } = await api.post("/admin/passkeys/register/options");
      const credential = await createPasskey(options);
      await api.post("/admin/passkeys/register", { credential, name: deviceName() });
      try { localStorage.setItem("vortex_passkey_on_device", "1"); } catch { /* storage blocked */ }
      toast.success(`${bio} is set up on this device.`);
      load();
    } catch (err) {
      if (!isCancel(err)) onAuthError(err, `Couldn't set up ${bio}.`);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (pk) => {
    if (!window.confirm(`Remove "${pk.name}"? That device will need the password and email code again.`)) return;
    try {
      await api.delete(`/admin/passkeys/${pk.id}`);
      toast.success(`"${pk.name}" removed.`);
      setPasskeys((prev) => prev.filter((p) => p.id !== pk.id));
    } catch (err) {
      onAuthError(err, "Could not remove passkey.");
    }
  };

  const signOutOthers = async () => {
    if (!window.confirm("Sign out every other device? This one stays signed in.")) return;
    try {
      const { data } = await api.post("/admin/sessions/sign-out-others");
      toast.success(data.signed_out ? `Signed out ${data.signed_out} other session(s).` : "No other devices were signed in.");
      load();
    } catch (err) {
      onAuthError(err, "Could not sign out other devices.");
    }
  };

  return (
    <div data-testid="security-page">
      <PageHeader title="Sign-in & Security"
        intro={`Set up ${bio} on each phone or computer you use, and sign in with one tap instead of the password and emailed code.`} />

      <section className={`${card} p-6`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="flex items-center gap-2 font-heading text-lg font-bold text-white">
            <Fingerprint size={18} className="text-vortex-cyan" /> Passkeys
          </h3>
          {passkeysSupported() ? (
            <button onClick={add} disabled={busy} className={ghostBtn} data-testid="passkey-add-button">
              <Plus size={14} /> {busy ? "Working..." : `Set up ${bio} on this device`}
            </button>
          ) : (
            <p className="text-sm text-slate-500">This browser can't create passkeys.</p>
          )}
        </div>
        {passkeys.length === 0 ? (
          <p className="mt-4 text-sm text-slate-400">No passkeys yet. Sign-in uses the password and emailed code.</p>
        ) : (
          <ul className="mt-4 divide-y divide-white/5">
            {passkeys.map((pk) => (
              <li key={pk.id} className="flex items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-100">{pk.name}</p>
                  <p className="text-xs text-slate-500">
                    Added {when(pk.created_at)} · Last used {when(pk.last_used_at)}
                    {pk.backed_up && " · Synced to your other devices"}
                  </p>
                </div>
                <button onClick={() => remove(pk)} aria-label={`Remove ${pk.name}`}
                  className="rounded-md p-2 text-slate-500 hover:bg-red-500/10 hover:text-red-300">
                  <Trash2 size={16} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className={`${card} mt-6 p-6`}>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="font-heading text-lg font-bold text-white">Signed-in devices</h3>
          <button onClick={signOutOthers} className={ghostBtn} data-testid="sign-out-others-button">
            <LogOut size={14} /> Sign out everywhere else
          </button>
        </div>
        <ul className="mt-4 divide-y divide-white/5">
          {sessions.map((s) => {
            const agent = describeAgent(s.user_agent);
            const Icon = agent.mobile ? Smartphone : Monitor;
            return (
              <li key={s.id} className="flex items-center gap-3 py-3">
                <Icon size={16} className="shrink-0 text-slate-500" />
                <div className="min-w-0">
                  <p className="text-sm text-slate-100">
                    {agent.label}
                    {s.current && <span className="ml-2 rounded-full bg-vortex-cyan/10 px-2 py-0.5 text-[10px] text-vortex-cyan">This device</span>}
                  </p>
                  <p className="text-xs text-slate-500">
                    Signed in {when(s.created_at)} with {s.method === "passkey" ? bio : s.method === "email_link" ? "an emailed link" : "password + code"}
                    {s.remember ? " · Kept signed in for 30 days" : ""}
                  </p>
                </div>
              </li>
            );
          })}
          {sessions.length === 0 && <li className="py-3 text-sm text-slate-400">Only this device.</li>}
        </ul>
      </section>
    </div>
  );
};

export default Security;
